import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  requiredRole?: string;
  requireAnyRole?: string[];
  /** If true, requires user to have isbeta="true" in their config (admins bypass this check) */
  requireBeta?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  redirectTo = "/login",
  requiredRole,
  requireAnyRole,
  requireBeta
}) => {
  const { isAuthenticated, isLoading, hasRole, hasAnyRole, getDefaultLandingPage, getUserConfigValue, isAdmin } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole && !hasRole(requiredRole)) {
    // User doesn't have required role, redirect to their default landing page
    const landingPage = getDefaultLandingPage();
    return <Navigate to={landingPage} replace />;
  }

  if (requireAnyRole && !hasAnyRole(requireAnyRole)) {
    // User doesn't have any of the required roles, redirect to their default landing page
    const landingPage = getDefaultLandingPage();
    return <Navigate to={landingPage} replace />;
  }

  // Check beta access requirement (admins bypass this check)
  if (requireBeta && !isAdmin()) {
    const isBetaUser = getUserConfigValue("isbeta") === "true";
    if (!isBetaUser) {
      return <Navigate to="/alpha" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
